import React, { useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { format, parseISO } from 'date-fns';

const FailedLoginDetailsTable = ({ failedLoginDetails }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterReason, setFilterReason] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 5;

    // Filter data based on search term and reason
    const filteredData = failedLoginDetails.filter(login => {
        const matchesSearch = 
            login.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            login.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            login.ip?.includes(searchTerm) ||
            login.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            login.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            login.connection?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesReason = filterReason === 'all' || login.reason === filterReason;
        
        return matchesSearch && matchesReason;
    });

    // Get unique reasons for filter dropdown
    const uniqueReasons = [...new Set(failedLoginDetails.map(login => login.reason))].filter(Boolean);

    // Reason styling
    const getReasonStyle = (reason) => {
        const styles = {
            'Invalid Credentials': { color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/20', icon: 'mdi:lock-alert' },
            'Account Locked': { color: 'text-red-600', bg: 'bg-red-200 dark:bg-red-900/30', icon: 'mdi:account-lock' },
            'User Not Found': { color: 'text-orange-600', bg: 'bg-orange-200 dark:bg-orange-900/30', icon: 'mdi:account-alert' },
            'MFA Required': { color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/20', icon: 'mdi:two-factor-authentication' },
            'Expired Password': { color: 'text-yellow-600', bg: 'bg-yellow-100 dark:bg-yellow-900/20', icon: 'mdi:key-alert' },
            'Too Many Attempts': { color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/20', icon: 'mdi:timer-alert' },
            'Account Suspended': { color: 'text-red-700', bg: 'bg-red-300 dark:bg-red-900/40', icon: 'mdi:account-cancel' },
            'Invalid Email': { color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/20', icon: 'mdi:email-alert' }
        };
        return styles[reason] || { color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-900/20', icon: 'mdi:alert-circle' };
    };

    // Pagination calculations
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);

    // Reset to page 1 when filters change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterReason]);

    return (
        <div className="rounded-md flex flex-col gap-3">
            <div className="flex items-center gap-1">
                <Icon icon="mdi:login-variant" className="text-xl text-red-500" />
                <h4 className="font-semibold text-font-primary">Failed Login Details</h4>
            </div>

            {/* Search and Filter Controls */}
            <div className="flex flex-col gap-3">
                {/* Search Bar */}
                <div>
                    <input
                        type="text"
                        placeholder="Search by reason, description, IP, user ID, email, or connection..."
                        className="w-full px-3 py-2 input-textbox rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                
                {/* Filter Controls */}
                <div className="flex flex-row gap-3 w-full">
                    <div className="w-full">
                        <label className="block text-xs text-font-secondary mb-1">Failure Reason</label>
                        <select
                            value={filterReason}
                            onChange={(e) => setFilterReason(e.target.value)}
                            className="w-full px-3 py-2 dropdown-container rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                            <option value="all">All Reasons</option>
                            {uniqueReasons.map(reason => (
                                <option key={reason} value={reason}>{reason}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            {filteredData.length === 0 ? (
                <div className="text-center py-12 flex flex-row gap-2 items-center justify-center">
                    <Icon icon="mdi:check-circle" className="text-3xl text-font-secondary" />
                    <p className="text-font-secondary text-lg font-semibold">
                        {failedLoginDetails.length === 0 
                            ? 'No failed login attempts detected' 
                            : 'No failed logins match your search criteria'
                        }
                    </p>
                </div>
            ) : (
                <div className="card-container overflow-x-auto p-0">
                    <table className="min-w-full divide-y divide-bgAccent">
                        <thead>
                            <tr>
                                <th className="px-5 py-3 text-left text-xs font-medium text-font-secondary uppercase tracking-wider">
                                    Date
                                </th>
                                <th className="px-5 py-3 text-left text-xs font-medium text-font-secondary uppercase tracking-wider">
                                    Reason
                                </th>
                                <th className="px-5 py-3 text-left text-xs font-medium text-font-secondary uppercase tracking-wider">
                                    Description
                                </th>
                                <th className="px-5 py-3 text-left text-xs font-medium text-font-secondary uppercase tracking-wider">
                                    Email / User ID
                                </th>
                                <th className="px-5 py-3 text-left text-xs font-medium text-font-secondary uppercase tracking-wider">
                                    IP Address
                                </th>
                                <th className="px-5 py-3 text-left text-xs font-medium text-font-secondary uppercase tracking-wider">
                                    Connection
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-bgAccent">
                            {paginatedData.map((login, index) => {
                                const reasonStyle = getReasonStyle(login.reason);
                                
                                return (
                                    <tr key={index} className="hover:bg-bgAccent transition-colors">
                                        <td className="px-5 py-3 whitespace-nowrap text-sm text-font-primary">
                                            {login.date ? format(parseISO(login.date), 'MMM d, yyyy HH:mm') : 'N/A'}
                                        </td>
                                        <td className="px-5 py-3 whitespace-nowrap text-sm">
                                            <div className="flex items-center gap-2">
                                                <Icon 
                                                    icon={reasonStyle.icon} 
                                                    className={`text-sm ${reasonStyle.color}`} 
                                                />
                                                <span className={`${reasonStyle.color} font-medium`}>
                                                    {login.reason || 'Unknown'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 text-sm text-font-secondary max-w-xs">
                                            <div className="truncate" title={login.description}>
                                                {login.description || 'No description available'}
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 text-sm text-font-primary">
                                            <div className="flex flex-col">
                                                {login.email && (
                                                    <span className="font-medium">{login.email}</span>
                                                )}
                                                {login.userId && (
                                                    <span className="text-xs text-font-secondary font-mono">{login.userId}</span>
                                                )}
                                                {!login.email && !login.userId && <span>N/A</span>}
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 whitespace-nowrap text-sm text-font-primary font-mono">
                                            {login.ip || 'N/A'}
                                        </td>
                                        <td className="px-5 py-3 whitespace-nowrap text-sm text-font-secondary">
                                            {login.connection || 'N/A'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination Controls */}
            {filteredData.length > 0 && (
                <div className="flex items-center justify-between px-5 pt-3 border-t border-bgAccent text-sm">
                    <div className="hidden sm:block">
                        <span className="text-font-secondary">
                            Showing <span className="font-semibold text-primary">{Math.min(startIndex + 1, filteredData.length)}</span> to <span className="font-semibold text-primary">{Math.min(startIndex + rowsPerPage, filteredData.length)}</span> of <span className="font-semibold text-primary">{filteredData.length}</span> results
                        </span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 px-1 sm:px-4 w-full sm:w-auto justify-between sm:justify-end">
                        <button
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                            className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                        >
                            <Icon icon="icon-park-outline:double-left" className="text-lg sm:text-xl text-accent" />
                        </button>
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                        >
                            <Icon icon="material-symbols:arrow-back-ios-new" className="text-base sm:text-lg text-accent" />
                        </button>
                        <span className="text-font-secondary text-xs sm:text-sm">
                            <span className="font-bold text-accent">{currentPage}</span>/<span className="font-bold">{totalPages}</span>
                        </span>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                        >
                            <Icon icon="material-symbols:arrow-forward-ios" className="text-base sm:text-lg text-accent" />
                        </button>
                        <button
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                            className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                        >
                            <Icon icon="icon-park-outline:double-right" className="text-lg sm:text-xl text-accent" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FailedLoginDetailsTable;

